import { useEffect } from 'react';

/**
 * Per-route SEO head sync — sets the document `<title>` and the
 * `<meta name="description">` content (plus the matching `og:title` /
 * `og:description` when those tags exist) for the current route.
 *
 * The app's `index.html` carries build-time defaults (CLAUDE.md
 * "index.html carries first-paint defaults" pattern); this hook rewrites
 * them per route once the SPA renders and restores the previous values on
 * unmount, so navigating between routes never leaves a stale title/description.
 *
 * SSR / first-paint safe: every DOM read is guarded, and a missing meta tag
 * is simply skipped rather than created — the build-time default stays.
 *
 * Pass `null`/`undefined` for a field whose data is still loading (e.g. an
 * async-loaded article title) — that field is left untouched until it resolves,
 * mirroring the original `useDocumentTitle(null)` contract.
 */
export interface UseSeoOptions {
  readonly title?: string | null;
  readonly description?: string | null;
}

function setMetaContent(selector: string, value: string): (() => void) | null {
  const el = document.querySelector(selector);
  if (!el) return null;
  const previous = el.getAttribute('content');
  el.setAttribute('content', value);
  return () => {
    if (previous !== null) el.setAttribute('content', previous);
  };
}

export function useSeo({ title = null, description = null }: UseSeoOptions): void {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const restores: (() => void)[] = [];

    if (title) {
      const previousTitle = document.title;
      document.title = title;
      restores.push(() => {
        document.title = previousTitle;
      });
      const restoreOgTitle = setMetaContent('meta[property="og:title"]', title);
      if (restoreOgTitle) restores.push(restoreOgTitle);
    }

    if (description) {
      const restoreDesc = setMetaContent('meta[name="description"]', description);
      if (restoreDesc) restores.push(restoreDesc);
      const restoreOgDesc = setMetaContent('meta[property="og:description"]', description);
      if (restoreOgDesc) restores.push(restoreOgDesc);
    }

    return () => {
      for (const restore of restores) restore();
    };
  }, [title, description]);
}
